import React, { useEffect, useRef } from 'react';
import appSettings from '../../../config/appSettings';
import styles from './GoogleAd.module.css';

const GoogleAd = ({ onAdLoaded, onAdCompleted, onAdError }) => {
  const adContainerRef = useRef(null);
  const isInitialized = useRef(false);
  const adsManagerRef = useRef(null);

  useEffect(() => {
    console.log('GoogleAd component mounted');
    
    // Prevent multiple initializations
    if (isInitialized.current) {
      console.log('Ad already being initialized, skipping');
      return;
    }
    
    // Load Google IMA SDK if not already loaded
    if (!window.google || !window.google.ima) {
      console.log('Loading Google IMA SDK script');
      const script = document.createElement('script');
      script.src = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';
      script.async = true;
      script.onload = () => {
        console.log('Google IMA SDK script loaded');
        setTimeout(() => {
          if (!isInitialized.current) {
            initializeAd();
          }
        }, 100); // Small delay to ensure DOM is ready
      };
      script.onerror = (e) => {
        console.error('Failed to load Google IMA SDK:', e);
        onAdError('Failed to load Google IMA SDK');
      };
      document.body.appendChild(script);
    } else {
      console.log('Google IMA SDK already loaded');
      setTimeout(() => {
        if (!isInitialized.current) {
          initializeAd();
        }
      }, 100); // Small delay to ensure DOM is ready
    }

    return () => {
      console.log('GoogleAd component unmounting');
      // Cleanup
      if (adsManagerRef.current) {
        console.log('Destroying ads manager');
        try {
          adsManagerRef.current.destroy();
        } catch (e) {
          console.error('Error destroying ads manager:', e);
        }
        adsManagerRef.current = null;
      }
      isInitialized.current = false;
    };
  }, [onAdError]);

  const initializeAd = () => {
    console.log('Initializing Google Ad...');
    if (isInitialized.current) {
      console.log('Ad already initialized, skipping');
      return;
    }
    
    if (!adContainerRef.current) {
      console.error('Ad container ref is null');
      return;
    }
    
    isInitialized.current = true;

    try {
      // Create a video element for the ad
      const videoElement = document.createElement('video');
      videoElement.style.width = '100%';
      videoElement.style.height = '100%';
      videoElement.muted = false;
      videoElement.playsInline = true;
      
      // Clear the container and add the video element
      while (adContainerRef.current.firstChild) {
        adContainerRef.current.removeChild(adContainerRef.current.firstChild);
      }
      adContainerRef.current.appendChild(videoElement);
      
      console.log('Creating AdDisplayContainer');
      const adDisplayContainer = new window.google.ima.AdDisplayContainer(
        adContainerRef.current,
        videoElement
      );
      
      // Initialize the container. Must be done via a user action on mobile devices.
      adDisplayContainer.initialize();
      
      // Create ads loader
      console.log('Creating AdsLoader');
      const adsLoader = new window.google.ima.AdsLoader(adDisplayContainer);
      
      // Add event listeners
      adsLoader.addEventListener(
        window.google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
        (adsManagerLoadedEvent) => {
          console.log('AdsManager loaded');
          
          // Get the ads manager
          const adsRenderingSettings = new window.google.ima.AdsRenderingSettings();
          adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true;
          
          // Get the ads manager
          const adsManager = adsManagerLoadedEvent.getAdsManager(
            videoElement, 
            adsRenderingSettings
          );
          adsManagerRef.current = adsManager;
          
          // Add event listeners
          adsManager.addEventListener(
            window.google.ima.AdEvent.Type.LOADED,
            () => {
              console.log('Ad loaded event');
              onAdLoaded();
            }
          );
          
          adsManager.addEventListener(
            window.google.ima.AdEvent.Type.COMPLETE,
            () => {
              console.log('Ad complete event');
              try {
                const adData = {
                  ad_id: adsManager.getCurrentAd().getAdId(),
                  instance_id: new Date().getTime().toString()
                };
                onAdCompleted(adData);
              } catch (e) {
                console.error('Error in ad complete handler:', e);
                onAdCompleted({
                  ad_id: 'unknown',
                  instance_id: new Date().getTime().toString()
                });
              }
            }
          );
          
          adsManager.addEventListener(
            window.google.ima.AdEvent.Type.ALL_ADS_COMPLETED,
            () => {
              console.log('All ads completed');
              onAdCompleted({
                ad_id: 'all_completed',
                instance_id: new Date().getTime().toString()
              });
            }
          );
          
          adsManager.addEventListener(
            window.google.ima.AdEvent.Type.ERROR,
            (event) => {
              console.error('Ad playback error:', event);
              const errorMessage = event.getError ? event.getError().getMessage() : 'Ad playback error';
              onAdError(errorMessage);
            }
          );
          
          // Handle window resize
          window.addEventListener('resize', () => {
            if (adsManager) {
              const width = adContainerRef.current.clientWidth;
              const height = adContainerRef.current.clientHeight;
              adsManager.resize(width, height, window.google.ima.ViewMode.NORMAL);
            }
          });
          
          try {
            // Initialize the ads manager. Width and height are required.
            const width = adContainerRef.current.clientWidth;
            const height = adContainerRef.current.clientHeight;
            console.log(`Initializing AdsManager with size: ${width}x${height}`);
            adsManager.init(width, height, window.google.ima.ViewMode.NORMAL);
            console.log('Starting ads');
            adsManager.start();
          } catch (adError) {
            console.error('Error initializing or starting ads:', adError);
            onAdError(adError);
          }
        }
      );
      
      adsLoader.addEventListener(
        window.google.ima.AdErrorEvent.Type.AD_ERROR,
        (adErrorEvent) => {
          console.error('Ad error event:', adErrorEvent.getError());
          const error = adErrorEvent.getError();
          const errorMessage = error ? error.getMessage() : 'Ad loading error';
          onAdError(errorMessage);
        }
      );
      
      // Request ads using configuration from appSettings
      const adsRequest = new window.google.ima.AdsRequest();
      
      // Build ad tag URL from configuration or use fallback
      let adTagUrl = appSettings.adProviders?.google?.adTagUrl;
      
      if (adTagUrl) {
        // Add correlator to prevent caching
        adTagUrl += Date.now();
      } else {
        // Fallback to test ad tag
        const safePageUrl = typeof window !== "undefined" && window.location?.href 
          ? window.location.href 
          : "https://example.com";
        
        const safePageTitle = typeof document !== "undefined" && document.title 
          ? document.title 
          : "GridRank Puzzle Game";
        
        adTagUrl = `https://pubads.g.doubleclick.net/gampad/ads?iu=/21775744923/external/single_ad_samples&sz=640x480&cust_params=sample_ct%3Dlinear&env=vp&output=vast&unviewed_position_start=1&url=${encodeURIComponent(safePageUrl)}&description_url=${encodeURIComponent(safePageTitle)}&correlator=${Date.now()}`;
      }
      
      adsRequest.adTagUrl = adTagUrl;

      // Specify the linear and nonlinear slot sizes. This helps the SDK
      // select the correct creative if multiple are returned.
      adsRequest.linearAdSlotWidth = 640;
      adsRequest.linearAdSlotHeight = 480;
      adsRequest.nonLinearAdSlotWidth = 640;
      adsRequest.nonLinearAdSlotHeight = 150;

      console.log('Requesting ads with tag URL:', adsRequest.adTagUrl);
      console.log('Sending ad request');
      adsLoader.requestAds(adsRequest);
    } catch (error) {
      console.error('Error in initializeAd:', error);
      onAdError(error);
    }
  };

  return (
    <div className={styles.googleAdContainer}>
      <div ref={adContainerRef} className={styles.adPlayer}></div>
    </div>
  );
};

export default GoogleAd;
